import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import type { Message, Artifact, CustomDataArtifact, FileArtifact as FileArtifactType } from '@dev-streetbeat/chat';
import { FinecoPortfolio } from 'pages/assistant/components/artifacts/components/fineco-portfolio/single/FinecoPortfolio';
import activityPanelStore from 'shared/store/activityPanelStore';
import { FinecoPortfolioStoryArtifact } from './components/fineco-portfolio-story-artifact/FinecoPortfolioStoryArtifact';
import { FinecoPortfolioCompare } from './components/fineco-portfolio/compare/FinecoPortfolioCompare';
import { FinecoProductComparisonArtifact } from './components/fineco-product-comparison-artifact/FinecoProductComparisonArtifact';
import FinecoModelPortfoliosArtifact from './components/fineco-model-portfolios-artifact/FinecoModelPortfoliosArtifact';
import FinecoProductCard from './components/fineco-product-card/single/FinecoProductCard';
import FinecoProductCardCompare from './components/fineco-product-card/compare/FinecoProductCardCompare';
import { FileArtifact } from './components/file-artifacts/FileArtifact';
import CandlestickChart from './components/candlestick-chart/CandlestickChart';

import './styles/styles.css';

type Props = {
  message: Message;
  finecoPortfolioAndPortfolioStoryTogether: boolean;
  artifact: Artifact;
  onLoaded?: () => void;
  isConnectionOpen: boolean;
  onSend: (text: string) => void;
  disabled: boolean;
};

export const MessageArtifact: React.FC<Props> = ({
  message,
  finecoPortfolioAndPortfolioStoryTogether,
  artifact,
  isConnectionOpen,
  onSend,
  disabled,
}) => {
  const params = useParams();
  const threadId = params.threadId;
  const setFinecoPortfolioArtifact = activityPanelStore((state) => state.setFinecoPortfolioArtifact);

  const finecoPortfolioWrapper = useMemo(() => {
    // Handle SDK CustomDataArtifact format for fineco_portfolio
    if (
      artifact.type === 'custom_data' &&
      (artifact as CustomDataArtifact).custom_data?.type === 'fineco_portfolio' &&
      (artifact as CustomDataArtifact).custom_data?.data !== null &&
      (artifact as CustomDataArtifact).custom_data?.data !== undefined
    ) {
      const customDataArtifact = artifact as CustomDataArtifact;
      const portfolioData = customDataArtifact.custom_data.data as any;
      const portfolioId = portfolioData.portfolio_id;

      return (
        <div style={{ width: '100%' }}>
          <div id={portfolioId?.toString()} className="fineco-portfolio-scroll-margin"></div>
          <FinecoPortfolio
            message={{ ...message, thread_id: threadId || '' }}
            finecoPortfolioAndPortfolioStoryTogether={finecoPortfolioAndPortfolioStoryTogether}
            artifact={customDataArtifact}
          />
        </div>
      );
    }
    return null;
  }, [artifact, message, finecoPortfolioAndPortfolioStoryTogether]);

  const type = artifact.type;
  useEffect(() => {
    // Handle SDK CustomDataArtifact format for fineco_portfolio
    if (
      type === 'custom_data' &&
      (artifact as CustomDataArtifact).custom_data?.type === 'fineco_portfolio' &&
      (artifact as CustomDataArtifact).custom_data?.data
    ) {
      setFinecoPortfolioArtifact(threadId, artifact);
    }
  }, [artifact, threadId, setFinecoPortfolioArtifact, type]);

  switch (true) {
    //** file */
    case type === 'file' &&
      (artifact as FileArtifactType).file !== null &&
      (artifact as FileArtifactType).file !== undefined:
      return <FileArtifact file={(artifact as FileArtifactType).file} />;

    //** fineco product card */
    case type === 'custom_data' &&
      (artifact as CustomDataArtifact).custom_data?.type === 'fineco_product_card' &&
      (artifact as CustomDataArtifact).custom_data?.data !== null &&
      (artifact as CustomDataArtifact).custom_data?.data !== undefined:
      return <FinecoProductCard data={(artifact as CustomDataArtifact).custom_data.data as any} />;

    //** fineco product card compare */
    case type === 'custom_data' &&
      (artifact as CustomDataArtifact).custom_data?.type === 'fineco_product_comparison' &&
      (artifact as CustomDataArtifact).custom_data?.data !== null &&
      (artifact as CustomDataArtifact).custom_data?.data !== undefined:
      return <FinecoProductCardCompare data={(artifact as CustomDataArtifact).custom_data.data as any} />;

    // ** fineco portfolio (SDK format)
    case type === 'custom_data' &&
      (artifact as CustomDataArtifact).custom_data?.type === 'fineco_portfolio' &&
      (artifact as CustomDataArtifact).custom_data?.data !== null &&
      (artifact as CustomDataArtifact).custom_data?.data !== undefined:
      return finecoPortfolioWrapper;

    //** portfolio story */
    case type === 'custom_data' &&
      (artifact as CustomDataArtifact).custom_data?.type === 'portfolio_story' &&
      !finecoPortfolioAndPortfolioStoryTogether &&
      (artifact as CustomDataArtifact).custom_data?.data !== null &&
      (artifact as CustomDataArtifact).custom_data?.data !== undefined:
      return <FinecoPortfolioStoryArtifact data={(artifact as CustomDataArtifact).custom_data.data} />;

    //** portfolio comparison */
    case type === 'custom_data' &&
      (artifact as CustomDataArtifact).custom_data?.type === 'fineco_portfolio_comparison' &&
      (artifact as CustomDataArtifact).custom_data?.data !== null &&
      (artifact as CustomDataArtifact).custom_data?.data !== undefined:
      return <FinecoPortfolioCompare data={(artifact as CustomDataArtifact).custom_data.data} artifact={artifact} />;

    //** portfolio product comparison */
    case type === 'custom_data' &&
      (artifact as CustomDataArtifact).custom_data?.type === 'product_comparison' &&
      (artifact as CustomDataArtifact).custom_data?.data !== null &&
      (artifact as CustomDataArtifact).custom_data?.data !== undefined:
      return <FinecoProductComparisonArtifact data={(artifact as CustomDataArtifact).custom_data.data} />;

    //** portfolio model comparison comparison */
    case type === 'custom_data' &&
      (artifact as CustomDataArtifact).custom_data?.type === 'fineco_model_portfolios' &&
      (artifact as CustomDataArtifact).custom_data?.data !== null &&
      (artifact as CustomDataArtifact).custom_data?.data !== undefined:
      return <FinecoModelPortfoliosArtifact data={(artifact as CustomDataArtifact).custom_data.data} onSend={onSend} />;

    //** candlestick chart */
    case type === 'custom_data' &&
      (artifact as CustomDataArtifact).custom_data?.type === 'candlestick_chart' &&
      (artifact as CustomDataArtifact).custom_data?.data !== null &&
      (artifact as CustomDataArtifact).custom_data?.data !== undefined:
      return <CandlestickChart data={(artifact as CustomDataArtifact).custom_data.data as any} />;
    default:
      return null;
  }
};
